import { localeMeta } from "@/lib/seo-head";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Check, Minus, Plus, RotateCcw } from "lucide-react";

import { IsoGrid, IsoIllustration, IsoStep } from "@/components/site/Iso";
import { RequestForm } from "@/components/site/RequestForm";
import { Reveal } from "@/components/site/Reveal";
import { Section, SectionHeading } from "@/components/site/Section";
import { Button } from "@/components/ui/button";
import { INDUSTRY_NAV } from "@/content/site";
import { getContent, getIndustryProducts } from "@/lib/cms.functions";
import type { IndustryProduct } from "@/lib/cms.types";
import { useT } from "@/lib/i18n-dict";
import { alternateLinks, localePath, normalizeLocale, SITE_URL } from "@/lib/i18n";
import { getPricing } from "@/lib/pricing.functions";
import { SHOW_PRICES } from "@/lib/pricing-visibility";
import {
  DEFAULT_SITE,
  MAX_SITES,
  decodeSites,
  encodeSites,
  normalizeSite,
  recommendSolution,
  summarizeRecommendation,
  type SiteScale,
} from "@/lib/solution-recommender";
import { cn } from "@/lib/utils";

const TITLE = "AI for manufacturing: find your AI-assisted MRP solution | factory.online";
const DESCRIPTION =
  "Choose your industry, the production challenges you want to solve and the size of each plant to see the matching factory.online plan, add-ons and options.";

type FinderSearch = {
  industry?: string | undefined;
  cases?: string | undefined;
  sites?: string | undefined;
  plan?: string | undefined;
};

const INDUSTRIES = INDUSTRY_NAV.filter((item) => item.to !== "/industries").map((item) => ({
  slug: item.to.split("/").pop() ?? "",
  label: item.label,
}));

const INDUSTRY_BLURB: Record<string, string> = {
  "cable-wire-manufacturing":
    "Complex BOMs with conductor, insulation and shielding layers; drum lengths, cutting waste and copper-price exposure drive margins. Best fit when costing and drum traceability hurt most.",
  "fiber-optic-cables":
    "Fixed-length fiber batches, coloring sequences and per-drum optical test records. Best fit when minimizing fiber waste and controlling coloring order are the priority.",
  "metals-mechanical-engineering":
    "MilTech and DefTech assembly: distributed sites, weekly engineering changes, serial-number traceability and kit completeness. Best fit when version control and multi-site visibility decide whether a batch ships.",
  "aluminum-extrusion":
    "Die management, press planning, alloy batches and cutting optimization. Best fit when die changeovers, billet traceability and remnant control slow production down.",
  "food-feed-production":
    "Recipes and yields, expiry dates, allergen control and lot traceability under audit pressure. Best fit for batch traceability and short-shelf-life planning.",
  "chemical-process-manufacturing":
    "Formulas, hazard classes, tank and vessel capacity, compliance documentation. Best fit for recipe versioning and safety-critical batch records.",
  "jobshop-custom-production":
    "Every order is unique: engineer-to-order, fast quoting, mixed low-volume batches. Best fit when quoting speed and per-order margin visibility decide who wins the deal.",
};

export const Route = createFileRoute("/{-$locale}/solution-finder")({
  staticData: { sitemap: true },
  validateSearch: (search: Record<string, unknown>): FinderSearch => ({
    industry: typeof search["industry"] === "string" ? search["industry"].slice(0, 100) : undefined,
    cases: typeof search["cases"] === "string" ? search["cases"].slice(0, 400) : undefined,
    sites: typeof search["sites"] === "string" ? search["sites"].slice(0, 200) : undefined,
    plan: typeof search["plan"] === "string" ? search["plan"].slice(0, 100) : undefined,
  }),
  loader: async ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const pricing = await getPricing({ data: { locale } });
    return { pricing, locale };
  },
  head: ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const path = localePath(locale, "/solution-finder");
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return {
      meta: [
        { title: __m.title },
        { name: "description", content: __m.description },
        { property: "og:title", content: __m.title },
        { property: "og:description", content: __m.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `${SITE_URL}${path}` },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: `${SITE_URL}${path}` },
        ...alternateLinks("/solution-finder"),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "factory.online Solution Finder",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description: DESCRIPTION,
            url: `${SITE_URL}${path}`,
          }),
        },
      ],
    };
  },
  component: SolutionFinderPage,
  errorComponent: () => (
    <Section>
      <p role="alert" className="text-center text-muted-foreground">
        The solution finder is temporarily unavailable. Please try again shortly.
      </p>
    </Section>
  ),
});

function SolutionFinderPage() {
  const t = useT();
  const { pricing, locale } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const industry = INDUSTRIES.find((item) => item.slug === search.industry);

  // Challenges load in the background so picking an industry reacts instantly.
  const productsQuery = useQuery<IndustryProduct[]>({
    queryKey: ["industry-products", industry?.slug ?? "", locale],
    enabled: Boolean(industry),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const item = await getContent({ data: { kind: "industry", slug: industry!.slug, locale } });
      if (!item) return [];
      return getIndustryProducts({ data: { itemId: item.id, locale } });
    },
  });
  const products = productsQuery.data ?? [];
  const selectedRefs = (search.cases ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  const activeRefs = selectedRefs.filter((ref) => products.some((product) => product.product_ref === ref));
  const sites = decodeSites(search.sites);

  const setSearch = (next: FinderSearch) =>
    navigate({ search: (previous) => ({ ...previous, ...next }), replace: true, resetScroll: false });

  const toggleCase = (ref: string) => {
    const next = activeRefs.includes(ref) ? activeRefs.filter((item) => item !== ref) : [...activeRefs, ref];
    setSearch({ cases: next.join(",") || undefined });
  };

  const updateSite = (index: number, patch: Partial<SiteScale>) => {
    const next = sites.map((site, i) => (i === index ? normalizeSite({ ...site, ...patch }) : site));
    setSearch({ sites: encodeSites(next) });
  };

  const addSite = () => {
    if (sites.length >= MAX_SITES) return;
    setSearch({ sites: encodeSites([...sites, { ...DEFAULT_SITE }]) });
  };

  const removeSite = (index: number) => {
    if (sites.length <= 1) return;
    setSearch({ sites: encodeSites(sites.filter((_, i) => i !== index)) });
  };

  const recommendation =
    industry && activeRefs.length
      ? recommendSolution({ pricing, products, selectedRefs: activeRefs, sites, planSlug: search.plan })
      : null;

  const message = industry && recommendation ? summarizeRecommendation(industry.label, recommendation) : "";
  const primaryPlan = recommendation?.sites[0]?.plan;

  return (
    <>
      <section className="relative overflow-hidden border-b border-border/70 py-16 md:py-24">
        <IsoGrid intensity="soft" />
        <div className="container-page relative grid items-center gap-10 lg:grid-cols-[1.05fr_.95fr]">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              {t("AI for manufacturing — solution finder")}
            </p>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-bold text-balance-tight md:text-6xl">
              {t("AI for manufacturing: find the right solution for your factory")}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              {t(
                "Pick your industry, mark every production challenge that hurts and describe the size of each plant. You get a concrete set — plan per plant, add-ons and quantity options — instead of a generic price list.",
              )}
            </p>
          </Reveal>
          <Reveal variant="iso" delay={80}>
            <IsoIllustration
              name="smartFactory"
              alt={t("Isometric smart factory with connected production operations")}
              className="mx-auto w-full max-w-xl"
              parallax={false}
            />
          </Reveal>
        </div>
      </section>

      <Section>
        <div className="grid items-start gap-10 xl:grid-cols-[minmax(0,1.15fr)_minmax(340px,.85fr)]">
          <div className="grid gap-10">
            <ChoiceSection n="1" title={t("Choose your industry")}>
              <div className="grid gap-3 sm:grid-cols-2">
                {INDUSTRIES.map((item) => (
                  <ChoiceButton
                    key={item.slug}
                    selected={industry?.slug === item.slug}
                    onClick={() => setSearch({ industry: item.slug, cases: undefined, plan: undefined })}
                    title={t(item.label)}
                    description={INDUSTRY_BLURB[item.slug] ? t(INDUSTRY_BLURB[item.slug] as string) : undefined}
                  />
                ))}
              </div>
            </ChoiceSection>

            <ChoiceSection
              n="2"
              title={t("Mark the production challenges you want to solve")}
              hint={t("Select as many as apply — each one adds the module or add-on that closes it.")}
              disabled={!industry}
            >
              <div className="grid gap-3">
                {products.map((product) => (
                  <ChoiceButton
                    key={product.id}
                    multi
                    selected={activeRefs.includes(product.product_ref)}
                    onClick={() => toggleCase(product.product_ref)}
                    title={t(product.pain)}
                    description={t(product.outcome)}
                  />
                ))}
                {industry && productsQuery.isPending ? (
                  <p className="text-muted-foreground">{t("Loading…")}</p>
                ) : null}
                {industry && !productsQuery.isPending && !products.length ? (
                  <p className="text-muted-foreground">
                    {t("Challenges for this industry are being prepared. Talk to a consultant and we will map them with you.")}
                  </p>
                ) : null}
              </div>
            </ChoiceSection>

            <ChoiceSection
              n="3"
              title={t("Describe your plants")}
              hint={t("Each plant gets its own subscription. Inside a plant we count accounting locations — shop areas and warehouses.")}
              disabled={!activeRefs.length}
            >
              <div className="grid gap-4">
                {sites.map((site, index) => (
                  <div key={index} className="border border-border bg-card p-5">
                    <div className="mb-4 flex items-center justify-between gap-4">
                      <p className="font-display text-lg font-semibold">
                        {t("Plant")} {index + 1}
                      </p>
                      {sites.length > 1 ? (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeSite(index)}>
                          <Minus aria-hidden /> {t("Remove")}
                        </Button>
                      ) : null}
                    </div>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <NumberField
                        label={t("Users")}
                        value={site.users}
                        min={1}
                        max={2000}
                        onChange={(users) => updateSite(index, { users })}
                      />
                      <NumberField
                        label={t("Work centers")}
                        value={site.workCenters}
                        min={1}
                        max={500}
                        onChange={(workCenters) => updateSite(index, { workCenters })}
                      />
                      <NumberField
                        label={t("Accounting locations")}
                        value={site.locations}
                        min={1}
                        max={200}
                        onChange={(locations) => updateSite(index, { locations })}
                      />
                    </div>
                  </div>
                ))}
                {sites.length < MAX_SITES ? (
                  <Button type="button" variant="outline" onClick={addSite} className="justify-center">
                    <Plus aria-hidden /> {t("Add plant")}
                  </Button>
                ) : null}
              </div>
            </ChoiceSection>
          </div>

          <aside className="xl:sticky xl:top-24">
            <div className="border border-border bg-card p-6 shadow-[var(--shadow-lift)] md:p-8">
              <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">{t("Your recommendation")}</p>
              {industry && recommendation ? (
                <div className="mt-5">
                  <p className="text-sm font-semibold text-muted-foreground">{industry.label}</p>
                  <h2 className="mt-2 font-display text-2xl font-bold">
                    {recommendation.totals.sites > 1
                      ? `${recommendation.totals.sites} ${t("plant subscriptions")}`
                      : (recommendation.sites[0]?.plan.name ?? "")}
                  </h2>

                  <div className="mt-5 grid gap-4">
                    {recommendation.sites.map((site) => (
                      <div key={site.index} className="border border-border/70 p-4">
                        <p className="text-xs font-semibold tracking-[0.15em] text-muted-foreground uppercase">
                          {t("Plant")} {site.index + 1}
                        </p>
                        <p className="mt-1 font-display text-xl font-bold">{site.plan.name}</p>
                        {SHOW_PRICES ? (
                          <p className="mt-1 text-lg font-semibold text-primary">
                            {site.plan.price} <span className="text-sm font-normal text-muted-foreground">{site.plan.period}</span>
                          </p>
                        ) : (
                          <p className="mt-1 text-lg font-semibold text-primary">{t("On request")}</p>
                        )}
                        <p className="mt-2 text-sm text-muted-foreground">
                          {site.scale.users} {t("users")} · {site.scale.workCenters} {t("work centers")} · {site.scale.locations}{" "}
                          {t("accounting locations")}
                        </p>
                        {site.metered.length ? (
                          <ul className="mt-3 grid gap-1.5">
                            {site.metered.map((line) => (
                              <li key={line.slug} className="flex gap-2 text-sm text-muted-foreground">
                                <Plus className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                                {t(line.label)}: {line.quantity}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ))}
                  </div>

                  {recommendation.addons.length ? (
                    <>
                      <div className="my-6 border-t border-border" />
                      <h3 className="font-display text-lg font-semibold">{t("Add-ons for your challenges")}</h3>
                      <ul className="mt-3 grid gap-3">
                        {recommendation.addons.map(({ product, addon }) => (
                          <li key={product.id} className="text-sm">
                            <span className="font-semibold text-foreground">{addon?.name ?? product.name}</span>
                            <span className="mt-1 block text-muted-foreground">{t(product.outcome)}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}

                  {recommendation.modules.length ? (
                    <>
                      <div className="my-6 border-t border-border" />
                      <h3 className="font-display text-lg font-semibold">{t("Modules in focus")}</h3>
                      <ul className="mt-3 grid gap-2">
                        {recommendation.modules.map((module) => (
                          <li key={module.id} className="flex gap-2 text-sm text-muted-foreground">
                            <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                            {module.name}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}

                  {recommendation.totals.sites > 1 ? (
                    <p className="mt-6 border-s-2 border-primary ps-4 text-sm text-muted-foreground">
                      {t("Total")}: {recommendation.totals.sites} {t("plants")} · {recommendation.totals.users} {t("users")} ·{" "}
                      {recommendation.totals.locations} {t("accounting locations")}
                      {recommendation.totals.extraUsers
                        ? ` · +${recommendation.totals.extraUsers} ${t("extra users")}`
                        : ""}
                      {recommendation.totals.extraLocations
                        ? ` · +${recommendation.totals.extraLocations} ${t("extra locations")}`
                        : ""}
                    </p>
                  ) : null}

                  <div className="mt-6">
                    <p className="text-xs font-semibold tracking-[0.15em] text-muted-foreground uppercase">
                      {t("Prefer another plan level?")}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant={search.plan ? "outline" : "secondary"}
                        size="sm"
                        onClick={() => setSearch({ plan: undefined })}
                      >
                        {t("Auto")}
                      </Button>
                      {pricing.plans.map((plan) => (
                        <Button
                          key={plan.id}
                          type="button"
                          variant={search.plan === plan.slug ? "secondary" : "outline"}
                          size="sm"
                          onClick={() => setSearch({ plan: plan.slug })}
                        >
                          {plan.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-5"
                    onClick={() => navigate({ search: {}, replace: true, resetScroll: false })}
                  >
                    <RotateCcw aria-hidden /> {t("Start again")}
                  </Button>
                </div>
              ) : (
                <div className="mt-8 py-8 text-center">
                  <IsoIllustration name="planner" alt="Isometric planning board" className="mx-auto max-w-60" parallax={false} />
                  <p className="mt-5 text-muted-foreground">
                    {t("Choose an industry and at least one production challenge to see a tailored set.")}
                  </p>
                </div>
              )}
            </div>
          </aside>
        </div>
      </Section>

      {industry && recommendation ? (
        <Section className="bg-surface" id="contact">
          <div className="mx-auto max-w-4xl">
            <SectionHeading
              eyebrow={t("Talk to a manufacturing expert")}
              title={t("Validate the recommendation on your real production data")}
              lead="Share your contact details. We will review the selected scenario and prepare a focused walkthrough, rollout approach and pricing estimate."
            />
            <div className="mt-10">
              <RequestForm
                key={`${industry.slug}-${activeRefs.join("_")}-${encodeSites(sites)}-${primaryPlan?.slug ?? "auto"}`}
                kind="demo"
                withMessage
                defaultIndustry={industry.label}
                hideIndustry
                defaultMessage={message}
                {...(primaryPlan ? { planSlug: primaryPlan.slug } : {})}
                submitLabel={t("Request my tailored walkthrough")}
                successTitle={t("Your selection is with our team")}
                successText={t("A manufacturing consultant will review it and contact you shortly.")}
              />
            </div>
          </div>
        </Section>
      ) : null}

      <Section>
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <SectionHeading
            eyebrow="AI in manufacturing, practically"
            title="How to choose AI software for manufacturing and an MRP that supports it"
            lead="Artificial intelligence in manufacturing only pays off when it sits on clean operational data — real orders, bills of materials, routings, batches and machine capacity. That is why AI agents work inside the MRP, not beside it."
          />
          <div className="grid gap-5 text-base leading-relaxed text-muted-foreground md:text-lg">
            <h3 className="font-display text-xl font-semibold text-foreground">{t("Start with one production constraint")}</h3>
            <p>{t("Pick the constraint that causes the most missed dates, excess stock or hidden margin loss. A focused first scope makes rollout faster and the effect of AI-assisted planning measurable instead of theoretical.")}</p>
            <h3 className="font-display text-xl font-semibold text-foreground">{t("Where AI helps first in a factory")}</h3>
            <p>{t("Typical AI use cases in manufacturing are demand and material planning, capacity scheduling, quoting and cost simulation, purchasing suggestions, quality and traceability checks. factory.online AI agents recommend actions on this data — planners and shop-floor leads keep the final decision.")}</p>
            <h3 className="font-display text-xl font-semibold text-foreground">{t("Validate on your own data")}</h3>
            <p>{t("This finder maps industry-specific scenarios to the matching plan and add-ons so the first conversation is concrete. A manufacturing expert then validates the recommendation against your plant, integrations and rollout priorities.")}</p>
          </div>
        </div>
      </Section>
    </>
  );
}

function ChoiceSection({
  n,
  title,
  hint,
  disabled,
  children,
}: {
  n: string;
  title: string;
  hint?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={cn("transition-opacity", disabled && "pointer-events-none opacity-45")} aria-disabled={disabled}>
      <div className="mb-5 flex items-center gap-4">
        <IsoStep n={n} />
        <h2 className="font-display text-2xl font-bold">{title}</h2>
      </div>
      {hint ? <p className="mb-5 text-muted-foreground">{hint}</p> : null}
      {children}
    </section>
  );
}

function ChoiceButton({
  selected,
  onClick,
  title,
  description,
  multi,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  description?: string | undefined;
  multi?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "h-auto min-h-16 w-full justify-between whitespace-normal rounded-md p-4 text-start",
        selected && "border-primary bg-primary/10 text-foreground ring-1 ring-primary",
      )}
    >
      <span>
        <span className="block text-base font-semibold">{title}</span>
        {description ? <span className="mt-1 block text-sm font-normal leading-relaxed text-muted-foreground">{description}</span> : null}
      </span>
      {selected ? (
        <Check className="size-5 shrink-0 text-primary" aria-hidden />
      ) : multi ? (
        <Plus className="size-5 shrink-0 text-muted-foreground" aria-hidden />
      ) : (
        <ArrowRight className="size-5 shrink-0 text-muted-foreground" aria-hidden />
      )}
    </Button>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="grid gap-2 text-sm">
      <span className="font-semibold text-muted-foreground">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
        className="h-11 w-full border border-border bg-background px-3 text-base text-foreground focus:border-primary focus:outline-none"
      />
    </label>
  );
}
